import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Meta, Title } from '@angular/platform-browser';
import {
  InputComponent,
  SelectComponent,
  AlertComponent,
} from '@out-web/components';
import { EnvironmentsService, ListingsService, IListing } from '@out-web/core';
import { decodeVIN } from '@out-web/core';
import { ButtonComponent, TermsComponent } from '@out-web/components';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { finalize } from 'rxjs';
import { AlertService } from '@out-web/core';

@Component({
  selector: 'out-your-tesla',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputComponent,
    SelectComponent,
    ButtonComponent,
    AlertComponent,
    TermsComponent,
  ],
  templateUrl: './your-tesla-component.html',
  styleUrl: './your-tesla-component.scss',
})
export class YourTeslaComponent implements OnInit {
  form: FormGroup;
  isSaveModalOpen = false;
  errorMessages: string[] = [];
  showTerms = false;
  hasAttemptedSubmit = false;
  isDraftSaving = false;
  isSavingToContinue = false;

  currentYear: number = new Date().getFullYear();
  cleanHistory = true;
  warranty = false;
  modifications = false;
  title = true;
  fsd = false;
  autopilot = false;

  private isSettingValue = false;

  readonly initialFormValues = {
    type: 'ForSale',
    price: {
      currency: 'USD',
      value: 0,
    },
    location: {
      city: '',
      state: '',
      country: 'USA',
    },
    content: {
      title: '',
      description: '',
      meta: '',
      video: {
        title: null,
        description: null,
        url: '',
      },
    },
    vehicle: {
      vin: '',
      condition: '',
      mileage: 0,
      make: 'Tesla',
      model: '',
      trim: '',
      year: '',
      lease: {
        monthsRemaining: 0,
      },
      specification: {
        colour: '',
      },
      features: {
        autopilot: '',
      },
      keyInfo: [],
    },
  };

  constructor(
    private fb: FormBuilder,
    private meta: Meta,
    private titleService: Title,
    private environmentService: EnvironmentsService,
    private location: Location,
    private router: Router,
    private listingService: ListingsService,
    private alertService: AlertService
  ) {
    this.options.country = [
      {
        label: this.countryMap[this.environmentService.country.toUpperCase()],
        value: this.environmentService.country,
      },
    ];

    this.form = this.fb.group({
      type: ['ForSale'],
      price: this.fb.group({
        currency: ['USD'],
        value: [null, [Validators.required, Validators.pattern('^[0-9]*$')]],
      }),
      location: this.fb.group({
        city: ['', [Validators.required]],
        state: ['', [Validators.required]],
        country: ['USA'],
      }),
      content: this.fb.group({
        title: [{ value: '', disabled: true }],
        description: ['', [Validators.required, Validators.minLength(100)]],
        meta: [''],
        video: this.fb.group({
          title: [null],
          description: [null],
          url: [''],
        }),
      }),
      vehicle: this.fb.group({
        vin: [
          '',
          [
            Validators.minLength(17),
            Validators.maxLength(17),
            Validators.required,
          ],
        ],
        condition: ['', [Validators.required]],
        mileage: [
          null,
          [
            Validators.required,
            Validators.pattern('^[0-9]*$'),
            Validators.max(999999),
          ],
        ],
        make: ['Tesla', [Validators.required]],
        model: ['', [Validators.required]],
        trim: [''],
        year: [
          '',
          [
            Validators.required,
            Validators.pattern('^[0-9]*$'),
            Validators.min(2008),
            Validators.max(this.currentYear),
          ],
        ],
        lease: this.fb.group({
          monthsRemaining: [0],
        }),
        specification: this.fb.group({
          colour: [''],
        }),
        features: this.fb.group({
          autopilot: [''],
        }),
        keyInfo: [[]],
      }),
    });
  }

  ngOnInit(): void {
    this.setMetaData();
    this.setupLiveValidation();
  }

  get titleComputed(): string {
    const model = this.form.get('vehicle.model')?.value;
    const year = this.form.get('vehicle.year')?.value;
    const colour = this.form.get('vehicle.specification.colour')?.value;
    return `V2 / ${model || ''} / ${year || ''} / ${colour || ''}`;
  }

  goBack() {
    this.location.back();
  }

  toggleSaveModal() {
    this.isSaveModalOpen = !this.isSaveModalOpen;
  };

  private setMetaData() {
    this.titleService.setTitle(
      'Sell My Tesla| Sell Your Tesla | Only Used Tesla'
    );
    this.meta.addTags([
      {
        name: 'description',
        property: 'description',
        content:
          'Only Used Tesal | A Place Exclusively Dedicated To Buying And Selling Used Teslas',
        vmid: 'description',
      },
      {
        name: 'og:description',
        property: 'og:description',
        content:
          'Only Used Tesla | A Place Exclusively Dedicated To Buying And Selling Used Teslas',
        hid: 'og:description',
      },
      {
        name: 'keywords',
        content:
          'Used Tesla, Buy Tesla Canada, Tesla Model 3, Tesla Model S, Used Electric Cars, Affordable Tesla, Certified Tesla',
      },
      {
        name: 'author',
        content: 'Only Used Tesla',
      },
    ]);
  }

  getControl(path: string): FormControl {
    const control = this.form.get(path);
    if (!control) {
      console.warn(`Control not found for path: ${path}`);
    }
    return control as FormControl;
  }

  onControlChange(controlPath: string, newValue: string | number) {
    const control = this.getControl(controlPath);
    let value = String(newValue);

    // Format mileage with commas
    if (controlPath === 'vehicle.mileage' || controlPath === 'price.value') {
      // Remove any non-digit characters
      const numericValue = value.replace(/\D/g, '');
      // Format with commas
      value = numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

      // Store the numeric value in the form
      if (!this.isSettingValue) {
        this.isSettingValue = true;
        control.setValue(numericValue);
        this.isSettingValue = false;
        return;
      }
    }

    // Handle other controls as before
    if (!this.isSettingValue && control.value !== value) {
      this.isSettingValue = true;
      control.setValue(value);
      this.isSettingValue = false;
    }
  }

  onVinInputChange(controlPath: string, newValue: string | number) {
    const control = this.getControl(controlPath);

    const value = String(newValue);

    // Check if we're currently setting the value
    if (!this.isSettingValue && control.value !== value) {
      this.isSettingValue = true;
      control.setValue(value);
      this.isSettingValue = false;
    }

    const decoded = decodeVIN(value);
    if (decoded) {
      for (const key in decoded) {
        if (Object.prototype.hasOwnProperty.call(decoded, key)) {
          const element = decoded[key];
          if (this.vinDecodingMap[key]) {
            this.onControlChange(this.vinDecodingMap[key], element);
          }
        }
      }
    }
  }

  vinDecodingMap: { [key: string]: string } = {
    '1-3': 'vehicle.make',
    '4': 'vehicle.model',
    '10': 'vehicle.year',
  };

  saveListingAndContinue() {
    this.form.markAllAsTouched();
    if (this.form.valid) {
      this.isSavingToContinue = true;
      const keyInfo = [];
      this.errorMessages = [];

      const model = this.form.get('vehicle.model');

      if (model?.value === 'CYBERTRUCK') {
        this.form.get('vehicle.trim')?.setValue('TRIM PENDING');
      }

      const titleControl = this.form.get('content.title');
      if (titleControl) {
        titleControl.enable();
        titleControl.setValue(this.titleComputed);
      }

      if (this.cleanHistory) keyInfo.push('CleanHistory');
      if (this.warranty) keyInfo.push('Warranty');
      if (this.modifications) keyInfo.push('Modifications');
      if (this.title) keyInfo.push('Title');
      if (this.fsd) keyInfo.push('FSD');

      this.form.get('vehicle.keyInfo')?.setValue(keyInfo);

      this.listingService
        .createListing(this.form.getRawValue())
        .pipe(finalize(() => (this.isSavingToContinue = false)))
        .subscribe({
          next: (response: IListing) => {
            window.scrollTo(0, 0);
            this.Alert('success', 'Success', 'Listing created successfully');
            this.router.navigate([
              '/your-tesla/edit/listing/images/' + response.id,
            ]);
          },
          error: (error: any) => {
            console.error('Error creating listing:', error);
            window.scrollTo(0, 0);
            this.Alert('error', 'Error', 'Error creating listing' + error);
          },
        });
    } else {
      this.collectErrors();
      window.scrollTo(0, 0);
    }
  }

  draftErrorCheck() {
    this.form.markAllAsTouched();
    if (this.form.valid) {
      this.toggleSaveModal();
    } else {
      this.collectErrors();
      window.scrollTo(0, 0);
    }
  }

  draftSave() {
    /* this.form.markAllAsTouched();
    if (this.form.valid) { */
    this.isDraftSaving = true;
    const keyInfo = [];
    this.errorMessages = [];

    const model = this.form.get('vehicle.model');

    if (model?.value === 'CYBERTRUCK') {
      this.form.get('vehicle.trim')?.setValue('TRIM PENDING');
    }

    const titleControl = this.form.get('content.title');
    if (titleControl) {
      titleControl.enable();
      titleControl.setValue(this.titleComputed);
    }

    if (this.cleanHistory) keyInfo.push('CleanHistory');
    if (this.warranty) keyInfo.push('Warranty');
    if (this.modifications) keyInfo.push('Modifications');
    if (this.title) keyInfo.push('Title');
    if (this.fsd) keyInfo.push('FSD');

    this.form.get('vehicle.keyInfo')?.setValue(keyInfo);

    this.listingService
      .createListing(this.form.getRawValue())
      .pipe(finalize(() => (this.isDraftSaving = false)))
      .subscribe({
        next: (response: IListing) => {
          console.log('Listing created successfully:', response.id);
          this.form.reset(this.initialFormValues);

          // Reset checkbox states
          this.cleanHistory = true;
          this.warranty = false;
          this.modifications = false;
          this.title = true;
          this.fsd = false;
          this.autopilot = false;

          // Disable title control
          const titleControl = this.form.get('content.title');
          if (titleControl) {
            titleControl.disable();
          }

          // Reset error messages
          this.errorMessages = [];

          // Close the modal
          this.isSaveModalOpen = false;
          this.isDraftSaving = false;

          // Scroll to top
          window.scrollTo(0, 0);
          this.Alert('warning', 'Incomplete Listing', 'Check My Draft in your Dashboard to complete this ad later');
        },
        error: (error: any) => {
          console.error('Error creating listing:', error);
          window.scrollTo(0, 0);
          this.Alert('error', 'Error', 'Error creating listing' + error);
        },
      });
    /* } else {
      this.collectErrors();
      window.scrollTo(0, 0);
    } */
  }

  collectErrors(formGroup: FormGroup = this.form): void {
    this.errorMessages = [];

    const collectErrorsRecursive = (
      formGroup: FormGroup,
      parentKey = ''
    ): void => {
      Object.entries(formGroup.controls).forEach(([key, control]) => {
        const fullKey = parentKey ? `${parentKey}.${key}` : key;
        const displayName = this.errorDisplayNames[key] || key;

        if (control instanceof FormGroup) {
          // Recurse into nested form groups
          collectErrorsRecursive(control, fullKey);
        } else if (control?.invalid && (control.dirty || control.touched)) {
          // Collect error for the specific control
          this.errorMessages.push(`${displayName} is not valid.`);
        }
      });
    };

    collectErrorsRecursive(formGroup);
  }

  setShowTerms() {
    this.showTerms = !this.showTerms;
  }

  options = {
    modelsOptions: [
      { label: 'Select a model', value: '' },
      { label: 'Model 3', value: 'Model 3' },
      { label: 'Model S', value: 'Model S' },
      { label: 'Model X', value: 'Model X' },
      { label: 'Model Y', value: 'Model Y' },
      { label: 'CYBERTRUCK', value: 'CYBERTRUCK' },
      { label: 'Roadster', value: 'Roadster' },
      { label: 'Roadster Sport', value: 'Roadster Sport' },
    ],
    condition: [
      { label: 'Select a condition', value: '' },
      { label: 'Excellent', value: 'Excellent' },
      { label: 'Very Good', value: 'VeryGood' },
      { label: 'Good', value: 'Good' },
      { label: 'Fair', value: 'Fair' },
      { label: 'Poor', value: 'Poor' },
    ],
    listingType: [
      { label: 'For Sale', value: 'ForSale' },
      { label: 'Lease Takeover', value: 'LeaseTakeover' },
    ],
    autopilotOptions: [
      { label: 'Select autopilot', value: '' },
      { label: 'Base Autopilot', value: 'Base Autopilot' },
      { label: 'Autopilot 1', value: 'Autopilot 1' },
      { label: 'Autopilot 2 (Enhanced)', value: 'Autopilot 2 (Enhanced)' },
      { label: 'Full Self Driving', value: 'Full Self Driving' },
      { label: 'HW-2.5', value: 'HW-2.5' },
      { label: 'HW-3.0', value: 'HW-3-0' },
      { label: 'Pre-Autopilot.', value: 'Pre-Autopilot.' },
    ],
    trim: [
      { label: 'Select a trim', value: '' },
      { label: '40', value: '40' },
      { label: '53kWh', value: '53kWh' },
      { label: '60', value: '60' },
      { label: '60D', value: '60D' },
      { label: '70', value: '70' },
      { label: '70D', value: '70D' },
      { label: '75', value: '75' },
      { label: '75D', value: '75D' },
      { label: '85', value: '85' },
      { label: '85D', value: '85D' },
      { label: '90', value: '90' },
      { label: '90D', value: '90D' },
      { label: '100D', value: '100D' },
      { label: 'Long Range', value: 'Long Range' },
      { label: 'Long Range (Raven)', value: 'Long Range (Raven)' },
      { label: 'Long Range AWD', value: 'Long Range AWD' },
      {
        label: 'Long Range AWD Performance',
        value: 'Long Range AWD Performance',
      },
      {
        label: 'Long Range Performance (Raven)',
        value: 'Long Range Performance (Raven)',
      },
      { label: 'Long Range Plus', value: 'Long Range Plus' },
      { label: 'Long Range RWD', value: 'Long Range RWD' },
      { label: 'Mid-Range', value: 'Mid-Range' },
      { label: 'Plaid', value: 'Plaid' },
      { label: 'P85', value: 'P85' },
      { label: 'P85+', value: 'P85+' },
      { label: 'P85D', value: 'P85D' },
      { label: 'P85DL', value: 'P85DL' },
      { label: 'P90', value: 'P90' },
      { label: 'P90D', value: 'P90D' },
      { label: 'P90DL', value: 'P90DL' },
      { label: 'P100D', value: 'P100D' },
      { label: 'P100DL', value: 'P100DL' },
      { label: 'Performance', value: 'Performance' },
      { label: 'Standard Range', value: 'Standard Range' },
      { label: 'Standard Range (Raven)', value: 'Standard Range (Raven)' },
      { label: 'Standard-Range-Plus', value: 'Standard-Range-Plus' },
    ],
    state: [
      { label: 'Select a state', value: '' },
      { label: 'Alabama', value: 'Alabama' },
      { label: 'Alaska', value: 'Alaska' },
      { label: 'Arizona', value: 'Arizona' },
      { label: 'Arkansas', value: 'Arkansas' },
      { label: 'California', value: 'California' },
      { label: 'Colorado', value: 'Colorado' },
      { label: 'Connecticut', value: 'Connecticut' },
      { label: 'Delaware', value: 'Delaware' },
      { label: 'District of Columbia', value: 'District of Columbia' },
      { label: 'Florida', value: 'Florida' },
      { label: 'Georgia', value: 'Georgia' },
      { label: 'Hawaii', value: 'Hawaii' },
      { label: 'Idaho', value: 'Idaho' },
      { label: 'Illinois', value: 'Illinois' },
      { label: 'Indiana', value: 'Indiana' },
      { label: 'Iowa', value: 'Iowa' },
      { label: 'Kansas', value: 'Kansas' },
      { label: 'Kentucky', value: 'Kentucky' },
      { label: 'Louisiana', value: 'Louisiana' },
      { label: 'Maine', value: 'Maine' },
      { label: 'Maryland', value: 'Maryland' },
      { label: 'Massachusetts', value: 'Massachusetts' },
      { label: 'Michigan', value: 'Michigan' },
      { label: 'Minnesota', value: 'Minnesota' },
      { label: 'Mississippi', value: 'Mississippi' },
      { label: 'Missouri', value: 'Missouri' },
      { label: 'Montana', value: 'Montana' },
      { label: 'Nebraska', value: 'Nebraska' },
      { label: 'Nevada', value: 'Nevada' },
      { label: 'New Hampshire', value: 'New Hampshire' },
      { label: 'New Jersey', value: 'New Jersey' },
      { label: 'New Mexico', value: 'New Mexico' },
      { label: 'New York', value: 'New York' },
      { label: 'North Carolina', value: 'North Carolina' },
      { label: 'North Dakota', value: 'North Dakota' },
      { label: 'Ohio', value: 'Ohio' },
      { label: 'Oklahoma', value: 'Oklahoma' },
      { label: 'Oregon', value: 'Oregon' },
      { label: 'Pennsylvania', value: 'Pennsylvania' },
      { label: 'Rhode Island', value: 'Rhode Island' },
      { label: 'South Carolina', value: 'South Carolina' },
      { label: 'South Dakota', value: 'South Dakota' },
      { label: 'Tennessee', value: 'Tennessee' },
      { label: 'Texas', value: 'Texas' },
      { label: 'Utah', value: 'Utah' },
      { label: 'Vermont', value: 'Vermont' },
      { label: 'Virginia', value: 'Virginia' },
      { label: 'Washington', value: 'Washington' },
      { label: 'West Virginia', value: 'West Virginia' },
      { label: 'Wisconsin', value: 'Wisconsin' },
      { label: 'Wyoming', value: 'Wyoming' },
    ],
    country: [
      {
        label: 'United States',
        value: 'USA',
      },
    ],
  };

  private countryMap: { [key: string]: string } = {
    CANADA: 'Canada',
    USA: 'United States',
    UK: 'United Kingdom',
  };

  private errorDisplayNames: { [key: string]: string } = {
    vin: 'VIN',
    year: 'Year',
    model: 'Model',
    colour: 'Color',
    trim: 'Trim',
    condition: 'Condition',
    mileage: 'Mileage',
    value: 'Asking price',
    listingType: 'Listing type',
    city: 'City',
    description: 'Summary',
    state: 'State',
    leaseRemaining: 'Lease remaining',
    autopilotVersion: 'Autopilot version',
  };

  Alert(
    alertType: 'error' | 'success' | 'info' | 'warning' | 'white',
    alertTitle: string,
    alertMessage: string
  ) {
    this.alertService.showAlert({
      title: alertTitle,
      message: alertMessage,
      type: alertType,
      duration: 8000,
      position: 'top-right',
    });
  }

  formatNumber(value: number | string): string {
    if (value === null || value === undefined || value === '') return '';
    const numericValue = Number(value);
    if (isNaN(numericValue)) return ''; // Return empty if not a valid number
    return numericValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  private setupLiveValidation(formGroup: FormGroup = this.form, parentKey = ''): void {
    Object.entries(formGroup.controls).forEach(([key, control]) => {
      const fullKey = parentKey ? `${parentKey}.${key}` : key;
      const displayName = this.errorDisplayNames[key] || key;

      if (control instanceof FormGroup) {
        this.setupLiveValidation(control, fullKey); // recurse into nested
      } else {
        control.statusChanges.subscribe(() => {
          if (!control.invalid) {
            // Remove any existing message for this field
            const message = `${displayName} is not valid.`;
            const index = this.errorMessages.indexOf(message);
            if (index !== -1) {
              this.errorMessages.splice(index, 1);
            }
          }
        });
      }
    });
  }

}
